import { AppSidebar } from "@/components/app-sidebar"
import { ChatPanel } from "@/components/feature/Chat/ChatPanel/ChatPanel"
import { ChatPanelButton } from "@/components/feature/Chat/ChatPanel/ChatPanelButton"
import { FloatingChatPanel } from "@/components/feature/Chat/ChatPanel/FloatingChatPanel"
import { AppSidebarV2 } from "@/components/layout/sidebar-v2/AppSidebarV2"
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable"
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar"
import { HEADER_HEIGHT, SIDEBAR_ICON_SIZE } from "@/constants/UiConstants"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { NotificationBell } from "@/features/notifications"
import {
  ONBOARDING_CELEBRATE_STEP_ID,
  ONBOARDING_DONE_STEP_ID,
  ONBOARDING_FIRST_ANSWER_SOURCE_STEP_ID,
  ONBOARDING_FIRST_ANSWER_STEP_ID,
  ONBOARDING_GENERATE_STEP_ID,
  ONBOARDING_QUESTION_ANSWERING_STEP_ID,
  useOnboarding,
} from "@/features/onboarding"
import { useIsMobile } from "@/hooks/use-mobile"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useLayoutStore } from "@/store/layoutStore"
import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useLocation } from "react-router"
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip"
import { LayoutHeader } from "./LayoutHeader"
import {
  LayoutShellContext,
  type SidebarDefault,
  useLayoutShell,
} from "./LayoutShellContext"

const SIDEBAR_MINIMIZED_STEP_IDS = new Set([
  ONBOARDING_GENERATE_STEP_ID,
  ONBOARDING_QUESTION_ANSWERING_STEP_ID,
  ONBOARDING_CELEBRATE_STEP_ID,
  ONBOARDING_FIRST_ANSWER_STEP_ID,
  ONBOARDING_FIRST_ANSWER_SOURCE_STEP_ID,
  ONBOARDING_DONE_STEP_ID,
])

// The chat panel mounts conditionally; separate saved layouts per panel set
// keep hiding it from overwriting its remembered width. In screen order.
const PANELS_WITH_CHAT = ["layout-main", "layout-chat"]
const PANELS_WITHOUT_CHAT = ["layout-main"]

export type LayoutProps = {
  children?: React.ReactNode | React.ReactNode[]
  headerLeftElement?: React.ReactNode
  headerRightElement?: React.ReactNode | React.ReactNode[]
  defaultSidebarState?: SidebarDefault
}

export function Layout({
  children,
  headerLeftElement,
  headerRightElement,
  defaultSidebarState,
}: LayoutProps) {
  const shell = useLayoutShell()
  const header = (
    <LayoutHeader
      left={headerLeftElement}
      right={headerRightElement}
      defaultSidebarState={defaultSidebarState}
    />
  )
  // Inside the app shell (AppShellRoute) a page's Layout only fills the
  // shell's header: the sidebar, header and chat panel stay mounted.
  if (shell) {
    return (
      <>
        {header}
        {children}
      </>
    )
  }
  return <LayoutShell header={header}>{children}</LayoutShell>
}

// Navigating used to remount the whole shell, which also closed the mobile
// sidebar sheet; the persistent shell keeps that behaviour explicitly.
function CloseMobileSidebarOnNavigate() {
  const { pathname } = useLocation()
  const { setOpenMobile } = useSidebar()
  useEffect(() => {
    setOpenMobile(false)
  }, [pathname, setOpenMobile])
  return null
}

function LayoutShell({
  header,
  children,
}: {
  header: ReactNode
  children?: ReactNode
}) {
  const { isSidebarOpen, setSidebarOpen, chatPanelMode, closeChatPanel } =
    useLayoutStore()
  const { translate } = useTranslation()
  const isMobile = useIsMobile()
  const location = useLocation()
  const isChatRoute = location.pathname.startsWith("/chat")

  useEffect(() => {
    if (isChatRoute && chatPanelMode !== "closed") {
      closeChatPanel()
    }
  }, [isChatRoute, chatPanelMode, closeChatPanel])

  const effectiveChatMode =
    isChatRoute || chatPanelMode === "closed"
      ? "closed"
      : isMobile && chatPanelMode === "floating"
        ? "docked"
        : chatPanelMode
  const showDockedPanel = effectiveChatMode === "docked" && !isMobile
  const showMobileOverlay = effectiveChatMode === "docked" && isMobile
  const showFloatingPanel = effectiveChatMode === "floating"

  const notificationsInAppEnabled = useFeatureFlag("NOTIFICATIONS_IN_APP")
  const sidechatEnabled = useFeatureFlag("ENABLE_SIDECHAT")
  const sidebarV2Enabled = useFeatureFlag("SIDEBAR_V2")
  const {
    isActive: isTourActive,
    currentStepIndex,
    getCurrentTour,
  } = useOnboarding()
  const currentTour = getCurrentTour()
  const currentStepId = currentTour?.steps?.[currentStepIndex]?.id
  const shouldMinimizeSidebarDuringTour = Boolean(
    isTourActive &&
      currentStepId &&
      SIDEBAR_MINIMIZED_STEP_IDS.has(currentStepId),
  )
  const isSidebarLockedClosed = shouldMinimizeSidebarDuringTour
  const isSidebarLockedOpen = isTourActive && !shouldMinimizeSidebarDuringTour
  const isSidebarLocked = isSidebarLockedClosed || isSidebarLockedOpen
  const effectiveSidebarOpen = isSidebarLockedClosed
    ? false
    : isSidebarLockedOpen
      ? true
      : isSidebarOpen

  const sidebarTriggerTooltip = useMemo(() => {
    if (isSidebarLockedClosed) {
      return translate("TOOLTIP/SIDEBAR_HIDDEN_DURING_TOUR")
    }
    if (isSidebarLockedOpen) {
      return translate("TOOLTIP/SIDEBAR_LOCKED_DURING_TOUR")
    }
    return effectiveSidebarOpen
      ? translate("TOOLTIP/CLOSE_SIDEBAR")
      : translate("TOOLTIP/OPEN_SIDEBAR")
  }, [
    isSidebarLockedClosed,
    isSidebarLockedOpen,
    effectiveSidebarOpen,
    translate,
  ])

  const handleSidebarOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (isSidebarLockedClosed) {
        setSidebarOpen(false)
        return
      }
      if (isSidebarLockedOpen) {
        setSidebarOpen(true)
        return
      }
      setSidebarOpen(nextOpen)
    },
    [isSidebarLockedClosed, isSidebarLockedOpen, setSidebarOpen],
  )

  // A page's preferred sidebar state, applied by its LayoutHeader on each
  // mount; a tour lock overrides it.
  const applySidebarDefault = useCallback(
    (state: SidebarDefault) => {
      setSidebarOpen(
        isSidebarLockedClosed
          ? false
          : isSidebarLockedOpen
            ? true
            : state === "open",
      )
    },
    [isSidebarLockedClosed, isSidebarLockedOpen, setSidebarOpen],
  )

  const [leftSlot, setLeftSlot] = useState<HTMLDivElement | null>(null)
  const [rightSlot, setRightSlot] = useState<HTMLDivElement | null>(null)
  const shell = useMemo(
    () => ({ leftSlot, rightSlot, applySidebarDefault }),
    [leftSlot, rightSlot, applySidebarDefault],
  )

  // The shell's own wrappers are overflow-hidden, but scrollIntoView can still
  // offset them; a remount used to reset that on every navigation.
  const contentRef = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    for (
      let element: HTMLElement | null = contentRef.current;
      element && element !== document.body;
      element = element.parentElement
    ) {
      element.scrollTop = 0
      element.scrollLeft = 0
    }
  }, [location.pathname])

  useEffect(() => {
    if (isSidebarLockedClosed) {
      if (isSidebarOpen) {
        setSidebarOpen(false)
      }
      return
    }
    if (isSidebarLockedOpen && !isSidebarOpen) {
      setSidebarOpen(true)
    }
  }, [
    isSidebarLockedClosed,
    isSidebarLockedOpen,
    isSidebarOpen,
    setSidebarOpen,
  ])

  return (
    <LayoutShellContext.Provider value={shell}>
      <SidebarProvider
        className={"h-svh overflow-hidden"}
        open={effectiveSidebarOpen}
        onOpenChange={handleSidebarOpenChange}
      >
        <CloseMobileSidebarOnNavigate />
        {sidebarV2Enabled ? (
          <AppSidebarV2
            isSidebarOpen={effectiveSidebarOpen}
            isSidebarLockedOpen={isSidebarLockedOpen}
          />
        ) : (
          <AppSidebar
            isSidebarOpen={effectiveSidebarOpen}
            isSidebarLockedOpen={isSidebarLockedOpen}
          />
        )}
        <SidebarInset className="relative min-w-0 overflow-hidden">
          <ResizablePanelGroup
            orientation="horizontal"
            autoSaveId="chat-panel"
            panelIds={showDockedPanel ? PANELS_WITH_CHAT : PANELS_WITHOUT_CHAT}
          >
            <ResizablePanel id="layout-main" minSize={50}>
              <div className="flex h-full min-w-0 flex-col overflow-hidden">
                <header
                  className="z-14 sticky top-0 flex shrink-0 items-center gap-2 border-b bg-card"
                  style={{ height: HEADER_HEIGHT }}
                >
                  <div className="flex flex-1 items-center gap-2 px-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <SidebarTrigger
                          style={{
                            height: SIDEBAR_ICON_SIZE,
                            width: SIDEBAR_ICON_SIZE,
                          }}
                          aria-disabled={isSidebarLocked}
                          disabled={isSidebarLocked}
                        />
                      </TooltipTrigger>
                      <TooltipContent className="border bg-secondary-foreground text-card shadow-md">
                        <p>{sidebarTriggerTooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                    {/* The current page's header content is portalled in here
                      (LayoutHeader). The left host also pushes the rest right. */}
                    <div
                      ref={setLeftSlot}
                      className="flex flex-1 items-center gap-2"
                    />
                    <div ref={setRightSlot} className="contents" />
                    {notificationsInAppEnabled ? <NotificationBell /> : null}
                    {!isChatRoute && sidechatEnabled ? (
                      <ChatPanelButton />
                    ) : null}
                  </div>
                </header>
                <div
                  ref={contentRef}
                  className={
                    "flex min-h-0 flex-1 flex-col overflow-hidden bg-card"
                  }
                >
                  {header}
                  {children}
                </div>
              </div>
            </ResizablePanel>
            {showDockedPanel ? (
              <>
                <ResizableHandle className="w-1.5 border-0 bg-transparent after:hidden data-[separator=active]:bg-primary/40 data-[separator=hover]:bg-primary/30" />
                <ResizablePanel
                  id="layout-chat"
                  data-chat-panel=""
                  defaultSize={33}
                  minSize={25}
                  maxSize={50}
                >
                  <div className="h-full bg-background p-2 pl-0">
                    <div className="h-full overflow-hidden rounded-lg border border-border">
                      <ChatPanel className="h-full" />
                    </div>
                  </div>
                </ResizablePanel>
              </>
            ) : null}
          </ResizablePanelGroup>
          {showFloatingPanel ? <FloatingChatPanel /> : null}
          {showMobileOverlay ? (
            <div className="fixed inset-0 z-40 bg-card">
              <ChatPanel className="h-full" />
            </div>
          ) : null}
        </SidebarInset>
      </SidebarProvider>
    </LayoutShellContext.Provider>
  )
}
